import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  Vazio,
} from '../componentes/base';
import { BotaoCopiar } from '../componentes/BotaoCopiar';
import { Caixa, Campo, Seletor } from '../componentes/campos';
import { Paginacao, type Paginado } from '../componentes/Paginacao';
import { data, moeda, plural, telefone as fmtTelefone } from '../util/formato';

type Vista = 'vencimentos' | 'fila';

interface Disparo {
  readonly id: string;
  readonly tipo: string;
  readonly dataAlvo: string;
  readonly status: string;
  readonly texto: string;
  readonly erro: string | null;
  readonly linkWhatsApp: string | null;
  readonly devedor: {
    id: string;
    nome: string;
    telefone: string | null;
    telefoneExtra: string | null;
    clinica: { id: string; nome: string };
  };
  readonly parcela: { numero: number; valor: number; vencimento: string } | null;
}

interface ParcelaDoDia {
  readonly id: string;
  readonly numero: number;
  readonly valor: number;
  readonly vencimento: string;
  readonly status: string;
  readonly diasAteVencer: number;
  readonly devedor: {
    id: string;
    nome: string;
    telefone: string | null;
    telefoneExtra: string | null;
    clinica: { id: string; nome: string };
  };
  readonly acordo: { quantidadeParcelas: number };
}

/** Gravidade → rótulo e cor. A ordem é a mesma da régua no servidor. */
const TIPOS: Record<string, { rotulo: string; cor: string }> = {
  ATRASO_GRAVE: { rotulo: 'Aviso final', cor: 'var(--color-vencido)' },
  ATRASO: { rotulo: 'Em atraso', cor: 'var(--color-vencido)' },
  VENCE_HOJE: { rotulo: 'Vence hoje', cor: 'var(--color-atencao)' },
  LEMBRETE_1: { rotulo: 'Vence amanhã', cor: 'var(--color-atencao)' },
  LEMBRETE_3: { rotulo: 'Faltam 3 dias', cor: 'var(--acento)' },
  LEMBRETE_7: { rotulo: 'Faltam 7 dias', cor: 'var(--acento)' },
  PRIMEIRO_CONTATO: { rotulo: 'Primeiro contato', cor: 'var(--texto-suave)' },
  RETOMADA: { rotulo: 'Retomada', cor: 'var(--texto-suave)' },
};

function hojeISO(): string {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}-${String(h.getDate()).padStart(2, '0')}`;
}

function linkWhatsApp(telefone: string | null | undefined): string | null {
  if (!telefone) return null;
  return `https://wa.me/${telefone}`;
}

export function PaginaCobrancas() {
  const [vista, setVista] = useState<Vista>('vencimentos');
  const [clinicaId, setClinicaId] = useState('');
  /* Na visão de vencimentos o padrão é HOJE — a pergunta natural é "quem cobrar
     hoje?". Na fila, vazio continua significando "hoje + o que ficou para trás". */
  const [dia, setDia] = useState(hojeISO);

  return (
    <>
      <CabecalhoDaPagina
        titulo="Cobranças"
        descricao={
          vista === 'vencimentos'
            ? 'Escolha o dia e veja quem tem parcela vencendo — é a lista para mandar mensagem'
            : 'Fila de mensagens prontas para despachar no WhatsApp'
        }
        acoes={
          <div className="flex rounded-md border p-0.5">
            <Botao
              tipo={vista === 'vencimentos' ? 'primario' : 'discreto'}
              onClick={() => setVista('vencimentos')}
              className="px-3 py-1.5 text-xs"
            >
              Quem vence no dia
            </Botao>
            <Botao
              tipo={vista === 'fila' ? 'primario' : 'discreto'}
              onClick={() => setVista('fila')}
              className="px-3 py-1.5 text-xs"
            >
              Fila de mensagens
            </Botao>
          </div>
        }
      />

      {vista === 'vencimentos' ? (
        <VistaVencimentos
          clinicaId={clinicaId}
          setClinicaId={setClinicaId}
          dia={dia}
          setDia={setDia}
        />
      ) : (
        <VistaFila clinicaId={clinicaId} setClinicaId={setClinicaId} />
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════ Vencimentos ══ */

function VistaVencimentos({
  clinicaId,
  setClinicaId,
  dia,
  setDia,
}: {
  clinicaId: string;
  setClinicaId: (v: string) => void;
  dia: string;
  setDia: (v: string) => void;
}) {
  const [pagina, setPagina] = useState(1);

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/clinicas/selecao'),
  });

  const { data: lista, isLoading } = useQuery({
    queryKey: ['parcelas', 'vencimento', pagina, clinicaId, dia],
    queryFn: () =>
      api.buscar<Paginado<ParcelaDoDia>>('/parcelas', {
        pagina,
        limite: 50,
        vencimento: dia,
        ...(clinicaId ? { clinicaId } : {}),
      }),
    enabled: Boolean(dia),
  });

  const itens = lista?.itens ?? [];

  return (
    <Cartao semPadding>
      <div className="flex flex-wrap items-end gap-3 p-5">
        <div className="w-60">
          <Seletor
            rotulo="Clínica"
            valor={clinicaId}
            aoMudar={(v) => {
              setClinicaId(v);
              setPagina(1);
            }}
            vazio="Todas as clínicas"
            opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
          />
        </div>
        <div className="w-44">
          <Campo
            rotulo="Dia do vencimento"
            tipo="date"
            valor={dia}
            aoMudar={(v) => {
              setDia(v);
              setPagina(1);
            }}
          />
        </div>
        {dia !== hojeISO() && (
          <Botao
            tipo="discreto"
            onClick={() => {
              setDia(hojeISO());
              setPagina(1);
            }}
            className="px-0 pb-2 text-xs"
          >
            Voltar para hoje
          </Botao>
        )}
      </div>

      <p className="border-t px-5 py-2 text-xs text-[color:var(--texto-tenue)]">
        Parcelas em aberto com vencimento em <strong>{data(dia)}</strong>
        {clinicaId ? ' nesta clínica' : ''}. Quem já pagou não aparece.
      </p>

      {isLoading ? (
        <Carregando />
      ) : itens.length === 0 ? (
        <Vazio
          titulo={`Nenhuma parcela vencendo em ${data(dia)}.`}
          saida={
            <span className="text-xs text-[color:var(--texto-tenue)]">
              Confira se o acordo tem o dia de cobrança certo na ficha do devedor.
            </span>
          }
        />
      ) : (
        <>
          <ul>
            {itens.map((p) => {
              const numero = p.devedor.telefone || p.devedor.telefoneExtra;
              const wa = linkWhatsApp(numero);
              const situacao =
                p.status === 'ATRASADO' || p.diasAteVencer < 0
                  ? { rotulo: 'Em atraso', cor: 'var(--color-vencido)' }
                  : p.diasAteVencer === 0
                    ? { rotulo: 'Vence hoje', cor: 'var(--color-atencao)' }
                    : { rotulo: 'A vencer', cor: 'var(--texto-suave)' };

              return (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/devedores/${p.devedor.id}`}
                        className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                      >
                        {p.devedor.nome}
                      </Link>
                      <Ficha cor={situacao.cor}>{situacao.rotulo}</Ficha>
                    </div>
                    <p className="mt-0.5 text-xs text-[color:var(--texto-suave)]">
                      <span className="font-mono">{fmtTelefone(numero)}</span>
                      {' · '}
                      {p.devedor.clinica.nome}
                      {' · '}
                      parcela {p.numero === 0 ? 'entrada' : p.numero}
                      {p.acordo.quantidadeParcelas > 0
                        ? ` de ${p.acordo.quantidadeParcelas}`
                        : ''}
                      {' · '}
                      <span className="tabular font-medium text-[color:var(--texto)]">
                        {moeda(p.valor)}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Link
                      to={`/devedores/${p.devedor.id}`}
                      className="rounded-md border px-2.5 py-1 text-xs text-[color:var(--texto)] hover:border-[color:var(--acento)] hover:text-[color:var(--acento)]"
                    >
                      Abrir ficha
                    </Link>
                    {wa && (
                      <a
                        href={wa}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-md border px-2.5 py-1 text-xs font-medium text-[color:var(--texto)] hover:border-[color:var(--acento)] hover:text-[color:var(--acento)]"
                      >
                        WhatsApp
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {lista && (
            <Paginacao
              dados={lista}
              aoMudarPagina={setPagina}
              nomeDoItem={['parcela', 'parcelas']}
            />
          )}
        </>
      )}
    </Cartao>
  );
}

/* ══════════════════════════════════════════════════════════════ Fila ══ */

function VistaFila({
  clinicaId,
  setClinicaId,
}: {
  clinicaId: string;
  setClinicaId: (v: string) => void;
}) {
  const clienteConsulta = useQueryClient();
  const [pagina, setPagina] = useState(1);
  /* Na fila, "" = hoje arrastando atrasados. Começamos sem dia fixo para manter
     o comportamento antigo desta aba. */
  const [diaFila, setDiaFila] = useState('');
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(new Set());
  const [aberto, setAberto] = useState<string | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);
  /* Separado do `resultado` de propósito: falha e sucesso não podem sair com a
     mesma cor numa tela que dispara mensagem para devedor. */
  const [falha, setFalha] = useState<string | null>(null);

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/clinicas/selecao'),
  });

  const { data: fila, isLoading } = useQuery({
    queryKey: ['fila', pagina, clinicaId, diaFila],
    queryFn: () =>
      api.buscar<Paginado<Disparo>>('/cobranca/fila', {
        pagina,
        limite: 25,
        ...(clinicaId ? { clinicaId } : {}),
        ...(diaFila ? { dia: diaFila } : {}),
      }),
  });

  const { data: provedor } = useQuery({
    queryKey: ['provedor'],
    queryFn: () =>
      api.buscar<{
        nome: string;
        enviaAutomaticamente: boolean;
        detalhe: string;
        crm: { nome: string } | null;
      }>('/cobranca/provedor'),
  });

  function recarregar() {
    void clienteConsulta.invalidateQueries({ queryKey: ['fila'] });
    void clienteConsulta.invalidateQueries({ queryKey: ['contagens'] });
    void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
    setSelecionados(new Set());
  }

  function avisarFalha(erro: unknown, padrao: string) {
    setResultado(null);
    setFalha(erro instanceof FalhaDaApi ? erro.message : padrao);
  }

  const gerar = useMutation({
    mutationFn: () =>
      api.criar<{ criados: number; jaExistiam: number; semTelefone: number }>(
        '/cobranca/fila/gerar',
      ),
    onSuccess: (r) => {
      setFalha(null);
      setResultado(
        r.criados > 0
          ? `${plural(r.criados, 'mensagem adicionada', 'mensagens adicionadas')} à fila.` +
              (r.semTelefone > 0
                ? ` ${plural(r.semTelefone, 'caso ficou de fora por não ter telefone', 'casos ficaram de fora por não ter telefone')}.`
                : '')
          : 'A fila de hoje já estava montada — nada foi duplicado.',
      );
      recarregar();
    },
    onError: (e) => avisarFalha(e, 'Não foi possível montar a fila.'),
  });

  const despachar = useMutation({
    mutationFn: (ids: string[]) =>
      api.criar<{ enviados: number; aguardandoEnvioManual: number; falhas: unknown[] }>(
        '/cobranca/fila/despachar',
        { disparoIds: ids },
      ),
    onSuccess: (r) => {
      const total = r.enviados + r.aguardandoEnvioManual;
      setFalha(null);
      setResultado(
        `${plural(total, 'mensagem marcada como enviada', 'mensagens marcadas como enviadas')}.` +
          (r.falhas.length > 0 ? ` ${r.falhas.length} falhou(ram) e seguem na fila.` : ''),
      );
      recarregar();
    },
    onError: (e) => avisarFalha(e, 'Não foi possível marcar as mensagens como enviadas.'),
  });

  const cancelar = useMutation({
    mutationFn: (ids: string[]) =>
      api.criar<{ cancelados: number }>('/cobranca/fila/cancelar', { disparoIds: ids }),
    onSuccess: (r) => {
      setFalha(null);
      setResultado(`${plural(r.cancelados, 'mensagem cancelada', 'mensagens canceladas')}.`);
      recarregar();
    },
    onError: (e) => avisarFalha(e, 'Não foi possível cancelar as mensagens.'),
  });

  const itens = fila?.itens ?? [];
  const todosMarcados = itens.length > 0 && itens.every((d) => selecionados.has(d.id));

  function alternar(id: string) {
    setSelecionados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[color:var(--texto-suave)]">
          {diaFila
            ? `Somente mensagens agendadas para ${data(diaFila)} — não é a lista de quem vence nesse dia`
            : provedor?.enviaAutomaticamente
              ? `Envio automático por ${provedor.nome}`
              : 'Revise, marque e despache — cada item abre no WhatsApp com o texto pronto'}
        </p>
        <Botao tipo="primario" onClick={() => gerar.mutate()} desabilitado={gerar.isPending}>
          {gerar.isPending ? 'Montando…' : 'Montar a fila de hoje'}
        </Botao>
      </div>

      {resultado && (
        <div className="mb-4">
          <Alerta tom="recebido">{resultado}</Alerta>
        </div>
      )}

      {falha && (
        <div className="mb-4">
          <Alerta tom="vencido">{falha}</Alerta>
        </div>
      )}

      {provedor && !provedor.enviaAutomaticamente && (
        <div className="mb-5">
          <Alerta>
            Nenhuma mensagem sai sozinha. Você marca quem deve receber e despacha — o
            envio automático depende da contratação do canal oficial do WhatsApp.
          </Alerta>
        </div>
      )}

      <Cartao semPadding>
        <div className="sticky top-0 z-10 flex flex-wrap items-end justify-between gap-4 rounded-t-md bg-[color:var(--superficie-elevada)] p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-60">
              <Seletor
                rotulo="Clínica"
                valor={clinicaId}
                aoMudar={(v) => {
                  setClinicaId(v);
                  setPagina(1);
                }}
                vazio="Todas as clínicas"
                opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
              />
            </div>
            <div className="w-44">
              <Campo
                rotulo="Dia da mensagem"
                tipo="date"
                valor={diaFila}
                aoMudar={(v) => {
                  setDiaFila(v);
                  setPagina(1);
                }}
              />
            </div>
            {diaFila && (
              <Botao
                tipo="discreto"
                onClick={() => {
                  setDiaFila('');
                  setPagina(1);
                }}
                className="px-0 pb-2 text-xs"
              >
                Voltar para a fila de hoje
              </Botao>
            )}
          </div>

          {selecionados.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-[color:var(--texto-suave)]">
                {plural(selecionados.size, 'selecionada', 'selecionadas')}
              </span>
              <Botao
                onClick={() => cancelar.mutate([...selecionados])}
                desabilitado={cancelar.isPending}
              >
                Cancelar
              </Botao>
              <Botao
                tipo="primario"
                onClick={() => despachar.mutate([...selecionados])}
                desabilitado={despachar.isPending}
              >
                {despachar.isPending ? 'Despachando…' : 'Marcar como enviadas'}
              </Botao>
            </div>
          )}
        </div>

        {isLoading ? (
          <Carregando />
        ) : itens.length === 0 ? (
          <Vazio
            titulo="Nenhuma mensagem pendente."
            saida={
              <span className="text-xs text-[color:var(--texto-tenue)]">
                Use &quot;Quem vence no dia&quot; para ver as parcelas, ou monte a fila de
                hoje para gerar as mensagens.
              </span>
            }
          />
        ) : (
          <>
            <div className="border-y bg-[color:var(--superficie-afundada)] px-5 py-2">
              <Caixa
                rotulo={
                  <span className="text-xs text-[color:var(--texto-suave)]">
                    Selecionar as {itens.length} desta página
                  </span>
                }
                marcado={todosMarcados}
                aoMudar={(marcar) =>
                  setSelecionados(marcar ? new Set(itens.map((d) => d.id)) : new Set())
                }
              />
            </div>

            <ul>
              {itens.map((disparo) => {
                const tipo = TIPOS[disparo.tipo] ?? {
                  rotulo: disparo.tipo,
                  cor: 'var(--texto-suave)',
                };
                const expandido = aberto === disparo.id;
                const numero = disparo.devedor.telefone || disparo.devedor.telefoneExtra;

                return (
                  <li key={disparo.id} className="border-b last:border-0">
                    <div className="flex items-start gap-3 px-5 py-3">
                      <span className="pt-0.5">
                        <Caixa
                          rotulo=""
                          marcado={selecionados.has(disparo.id)}
                          aoMudar={() => alternar(disparo.id)}
                        />
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/devedores/${disparo.devedor.id}`}
                            className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                          >
                            {disparo.devedor.nome}
                          </Link>
                          <Ficha cor={tipo.cor}>{tipo.rotulo}</Ficha>
                          {disparo.dataAlvo < hojeISO() && (
                            <span className="text-xs" style={{ color: 'var(--color-atencao)' }}>
                              de {data(disparo.dataAlvo)}, ainda não despachada
                            </span>
                          )}
                        </div>

                        <p className="mt-0.5 text-xs text-[color:var(--texto-suave)]">
                          <span className="font-mono">{fmtTelefone(numero)}</span>
                          {' · '}
                          {disparo.devedor.clinica.nome}
                          {disparo.parcela && (
                            <>
                              {' · '}parcela {disparo.parcela.numero} de{' '}
                              {moeda(disparo.parcela.valor)}, vence{' '}
                              {data(disparo.parcela.vencimento)}
                            </>
                          )}
                        </p>

                        {disparo.erro && (
                          <p className="mt-1 text-xs" style={{ color: 'var(--color-vencido)' }}>
                            Falha no último envio: {disparo.erro}
                          </p>
                        )}

                        <button
                          type="button"
                          onClick={() => setAberto(expandido ? null : disparo.id)}
                          className="mt-1.5 text-xs text-[color:var(--texto-tenue)] hover:text-[color:var(--acento)]"
                        >
                          {expandido ? '▾ ocultar a mensagem' : '▸ ver a mensagem'}
                        </button>

                        {expandido && (
                          <pre className="mt-2 max-w-2xl rounded border bg-[color:var(--superficie-afundada)] p-3 font-sans text-xs leading-relaxed whitespace-pre-wrap text-[color:var(--texto-suave)]">
                            {disparo.texto}
                          </pre>
                        )}
                      </div>

                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <BotaoCopiar texto={disparo.texto} rotulo="Copiar mensagem" />
                        {provedor?.crm && (
                          <a
                            href={`/api/crm/devedores/${disparo.devedor.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium text-[color:var(--texto)] transition-colors hover:border-[color:var(--acento)] hover:text-[color:var(--acento)]"
                          >
                            Abrir no {provedor.crm.nome}
                          </a>
                        )}
                        {disparo.linkWhatsApp && (
                          <a
                            href={disparo.linkWhatsApp}
                            target="_blank"
                            rel="noreferrer"
                            className="shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium text-[color:var(--texto)] transition-colors hover:border-[color:var(--acento)] hover:text-[color:var(--acento)]"
                          >
                            Abrir no WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {fila && (
              <Paginacao
                dados={fila}
                aoMudarPagina={setPagina}
                nomeDoItem={['mensagem', 'mensagens']}
              />
            )}
          </>
        )}
      </Cartao>
    </>
  );
}
